<script setup>
import { computed, ref } from 'vue'
import CommentCard from './CommentCard.vue'
import { useToastStore } from '@/stores/Toast'
import { useLoadCommentsStore } from '@/stores/LoadComments'
import ConfirmationDialog from '@/components/shared/ConfirmationDialog.vue'

/**
 * The postman available id.
 *
 * @type {Number}
 */
const POSTMAN_AVAILABLE_ID = 526244

/**
 * The component's props.
 */
const props = defineProps({
    /**
     * The load id.
     *
     * @type {Number}
     */
    loadId: {
        type: Number,
        required: true,
    },
})

/**
 * The store instances.
 */
const toastStore = useToastStore()
const store = useLoadCommentsStore()

/**
 * The local refs.
 */
const activeId = ref(null)
const isLoading = ref(false)
const isDeleteLoading = ref(false)
const showConfirmation = ref(false)

/**
 * Get the load comments.
 *
 * @returns {Array}
 */
const comments = computed(() => {
    const id =
        process.env.NODE_ENV === 'development'
            ? POSTMAN_AVAILABLE_ID
            : props.loadId

    return store.filterByLoadId(id)
})

/**
 * Handle the delete action.
 *
 * @param {Number} id
 * @return {void}
 */
const onDelete = async (id) => {
    activeId.value = id
    showConfirmation.value = true
}

/**
 * Delete the comment.
 *
 * @return {void}
 */
const onDeleteComment = async () => {
    try {
        isDeleteLoading.value = true

        await store.delete(activeId.value)

        showConfirmation.value = false

        toastStore.setNotification({
            message: `Comment #${activeId.value} has been deleted.`,
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        isDeleteLoading.value = false
    }
}

/**
 * Handle the edit action.
 *
 * @param {Object} payload.id
 * @param {Object} payload.comment
 * @param {Object} payload.priority
 * @return {void}
 */
const onEdit = async (data) => {
    try {
        isLoading.value = true

        await store.update({
            id: data.id,
            comment: data.comment,
            priority: data.priority,
        })

        toastStore.setNotification({
            message: `Comment has been updated.`,
            type: 'success',
        })
    } catch (error) {
        console.log(error)

        toastStore.setNotification({
            message: `Comment could not be updated.`,
            type: 'error',
        })
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div class="flex flex-col h-full w-full">
        <div class="px-6 pt-6 h-full overflow-scroll">
            <comment-card
                class="mb-4"
                v-for="comment in comments"
                :key="comment.id"
                :comment="comment"
                @delete="onDelete"
                @edit="onEdit"
            />
        </div>
    </div>
    <confirmation-dialog
        v-model="showConfirmation"
        @confirm="onDeleteComment"
        :is-loading="isDeleteLoading"
    >
        <template #title>
            <div class="text-lg font-bold">Confirmation</div>
        </template>
        <template #body>
            <div class="text-sm">
                Are you sure you want to delete comment with ID #{{ activeId }}?
            </div>
        </template>
    </confirmation-dialog>
</template>
